import api from '@pray/shared/utils/api';
import type { ObjectResponse, PaginatedListResponse } from '@pray/shared/utils/api/types/response';

import type {
  CreateStudioMediaAssetUploadInput,
  StudioMediaAsset,
  StudioMediaAssetUploadCompletion,
  StudioMediaAssetUploadIntent,
} from './types';

export type { StudioMediaAsset } from './types';

export type StudioMediaAssetStats = {
  total_assets: number;
  total_folders: number;
  total_size_bytes: number;
};

export type StudioMediaAssetsPage = PaginatedListResponse<StudioMediaAsset> & {
  folders: string[];
  stats: StudioMediaAssetStats;
  total: number;
};

export async function getMediaAssets({
  artistId,
  cursor,
  limit = 48,
  q,
  folderPath,
}: {
  artistId: string;
  cursor?: string;
  limit?: number;
  q?: string;
  folderPath?: string;
}): Promise<StudioMediaAssetsPage> {
  if (!artistId) throw new Error('Artist ID is required');

  const response = await api.executeGetRequest<StudioMediaAssetsPage>(`/web/studio/artists/${artistId}/media-assets`, {
    cursor,
    limit,
    q,
    folder_path: folderPath,
  });

  return response.data;
}

export async function createMediaAssetUpload({
  artistId,
  fileName,
  contentType,
  sizeBytes,
  width,
  height,
  folderPath,
}: CreateStudioMediaAssetUploadInput): Promise<StudioMediaAssetUploadIntent> {
  if (!artistId) throw new Error('Artist ID is required');

  const response = await api.executePostRequest<ObjectResponse<StudioMediaAssetUploadIntent>>(
    `/web/studio/artists/${artistId}/media-assets/uploads`,
    {
      file_name: fileName,
      content_type: contentType,
      size_bytes: sizeBytes,
      width,
      height,
      folder_path: folderPath,
    }
  );

  return response.data.data;
}

export async function updateMediaAssetFolder({
  artistId,
  assetId,
  folderPath,
}: {
  artistId: string;
  assetId: string;
  folderPath: string | null;
}): Promise<{ id: string; folder_path: string | null }> {
  if (!artistId) throw new Error('Artist ID is required');
  if (!assetId) throw new Error('Asset ID is required');

  const response = await api.executePatchRequest<ObjectResponse<{ asset: { id: string; folder_path: string | null } }>>(
    `/web/studio/artists/${artistId}/media-assets/${assetId}/folder`,
    {
      folder_path: folderPath,
    }
  );

  return response.data.data.asset;
}

export async function createMediaFolder({
  artistId,
  path,
}: {
  artistId: string;
  path: string;
}): Promise<{ id: string; path: string }> {
  if (!artistId) throw new Error('Artist ID is required');

  const response = await api.executePostRequest<ObjectResponse<{ folder: { id: string; path: string } }>>(
    `/web/studio/artists/${artistId}/media-assets/folders`,
    { path }
  );

  return response.data.data.folder;
}

export async function completeMediaAssetUpload({
  artistId,
  assetId,
}: {
  artistId: string;
  assetId: string;
}): Promise<StudioMediaAssetUploadCompletion> {
  if (!artistId) throw new Error('Artist ID is required');
  if (!assetId) throw new Error('Asset ID is required');

  const response = await api.executePostRequest<ObjectResponse<StudioMediaAssetUploadCompletion>>(
    `/web/studio/artists/${artistId}/media-assets/${assetId}/complete`
  );

  return response.data.data;
}
